import { React, useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import { nanoid } from 'nanoid';

import { ReactComponent as PlusIcon } from '../../../../../assets/images/AddButton.svg';
import { ReactComponent as ColoredLinkedlnIconImage } from '../../../../../assets/images/ColoredLinkedlnIconImage.svg';
import { ReactComponent as CrossIcon } from '../../../../../assets/images/CrossIcon.svg';
import { ReactComponent as PersonIcon } from '../../../../../assets/images/PersonIcon.svg';
import { ReactComponent as PositionIcon } from '../../../../../assets/images/position.svg';
import { ReactComponent as PublicIconImage } from '../../../../../assets/images/PublicIconImage.svg';
import { ReactComponent as PublicOffIconImage } from '../../../../../assets/images/PublicOffIconImage.svg';
import Notification from '../../../../../components/Notification/Notification';
import Button from '../../../../../components/shared/Button/Button';
import CustomTextField from '../../../../../components/shared/CustomTextField/CustomTextField';
import LocationSelect from '../../../../../components/shared/Location/LocationSelect';
import { USER_PROFILE_REGEX } from '../../../../../constants/commonConstants';
import { validateLinkedinLink, validateWebsiteLinks, validateWebsiteURL } from '../../../../../helpers/commonHelper';
import { IsMobile } from '../../../../../helpers/IsMobile';
import newUserAppUrl from '../../../../../routing/newUserApp';
import urls from '../../../../../routing/urls';

import styles from './EditProfileBio.module.scss';

const FORMATTED_URL = newUserAppUrl.split('//')[1];

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  location: '',
  linkedin: '',
  company_position: '',
  personal_url: '',
};

export const EditProfileBio = ({ showModal, setShowModal, currentUser, handleProfileUpdate }) => {
  const { t } = useTranslation('common');
  const linkedinAbbreviation = 'ln';
  const isMobile = IsMobile();
  const navigate = useNavigate();
  const formRef = useRef();
  const [initialFormValues, setInitialFormValues] = useState(INITIAL_VALUES);
  const [websiteLinks, setWebsiteLinks] = useState([]);
  const [customWebsiteLink, setCustomWebsiteLink] = useState({ type: '', value: '', public: true });
  const [isProfileURLDuplicate, setIsProfileURLDuplicate] = useState(false);
  const linkedin = websiteLinks.filter((link) => link.type === linkedinAbbreviation);

  useEffect(() => {
    if (currentUser?.links_json) {
      setWebsiteLinks(JSON.parse(currentUser?.links_json));
    }
    setCustomWebsiteLink({ type: '', value: '', public: true });
  }, [currentUser?.links_json, showModal]);

  useEffect(() => {
    if (currentUser) {
      setInitialFormValues({
        ...initialFormValues,
        first_name: currentUser.first_name?.trim(),
        last_name: currentUser.last_name?.trim(),
        company_position: currentUser.company_position,
        personal_url: currentUser.personal_url,
        location: { full_address: currentUser.full_address },
        websiteLinks,
      });
    }
  }, [currentUser, showModal]);

  const handleLinkedinValue = useCallback(
    (event, id) => {
      const isNew = websiteLinks.filter((link) => link.type === id);
      let data = '';
      if (isNew.length) {
        data = websiteLinks.map((element) =>
          element.type === id ? { ...element, value: event.target.value } : element
        );
      } else {
        data = websiteLinks.concat({ type: id, value: event.target.value });
      }
      setWebsiteLinks(data);
    },
    [setWebsiteLinks, websiteLinks]
  );

  const handleLinkedinVisibilityChange = useCallback((isPublic) => {
    setWebsiteLinks((prevState) =>
      prevState.map((link) => (link.type === 'ln' ? { ...link, public: isPublic } : link))
    );
  }, []);

  const handleChange = useCallback(
    (event, id) => {
      const data = websiteLinks.map((element) =>
        element.type === id ? { ...element, value: event.target.value } : element
      );
      setWebsiteLinks(data);
    },
    [setWebsiteLinks, websiteLinks]
  );

  const handleVisibilityChange = useCallback((id, isPublic) => {
    setWebsiteLinks((prevState) => prevState.map((link) => (link.type === id ? { ...link, public: isPublic } : link)));
  }, []);

  const customWebsiteHandleChange = useCallback((event, id) => {
    setCustomWebsiteLink((prevState) => ({ type: id, value: event.target.value, public: prevState.public }));
  }, []);

  const handleRemove = useCallback(
    (type) => {
      setWebsiteLinks(websiteLinks.filter((element) => element.type !== type));
    },
    [setWebsiteLinks, websiteLinks]
  );

  const addNewWebsitelink = useCallback(() => {
    if (customWebsiteLink.value) {
      if (validateWebsiteURL(customWebsiteLink.value)) {
        setWebsiteLinks([...websiteLinks, customWebsiteLink]);
        setCustomWebsiteLink({ type: '', value: '', public: true });
      } else {
        Notification.error(t('validation.invalidWebsiteLink'));
      }
    } else {
      Notification.error(t('validation.cannotAddEmpty'));
    }
  }, [customWebsiteLink, t]);

  const onSubmitSuccess = useCallback((url) => {
    setShowModal(false);
    if (url) {
      navigate(`${urls.profile}/${url}`);
    }
  }, []);

  const onUrlDuplicate = () => {
    setIsProfileURLDuplicate(true);
  };

  const handleSubmit = useCallback(
    (values) => {
      let websiteData = [...websiteLinks];
      const { personal_url } = values;
      if (personal_url && !USER_PROFILE_REGEX.test(personal_url)) {
        return Notification.error(t('validation.profileUrlInvalidFormat'));
      }
      if (!validateLinkedinLink(websiteData)) {
        return Notification.error(t('validation.invalidLinkedinUrl'));
      }
      if (!validateWebsiteLinks(websiteData)) {
        return Notification.error(t('validation.invalidWebsiteLink'));
      }
      if (customWebsiteLink.value) {
        if (validateWebsiteURL(customWebsiteLink.value)) {
          websiteData = [...websiteData, customWebsiteLink];
        } else {
          return Notification.error(t('validation.invalidWebsiteLink'));
        }
      }
      const newValues = {
        ...values,
        ...values.location,
        zip: values.location?.zip_code,
        current_job_lat: values.location?.lat,
        current_job_lon: values.location?.lon,
        country: values?.country_name,
        links_json: JSON.stringify(websiteData),
      };
      delete newValues.location;
      delete newValues.zip_code;
      delete newValues.lat;
      delete newValues.lon;
      delete newValues.country_name;
      delete newValues.websiteLinks;
      handleProfileUpdate(newValues, onSubmitSuccess, onUrlDuplicate);
    },
    [websiteLinks, customWebsiteLink]
  );

  const handlePersonalURLFocus = () => {
    setIsProfileURLDuplicate(false);
  };

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size="lg"
      fullscreen={isMobile}
      className={styles.container}
    >
      <Modal.Header className={styles.modalHeader} closeButton>
        <Modal.Title className={styles.modalTitle}>{t('labels.personalInfo')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik initialValues={initialFormValues} innerRef={formRef} onSubmit={handleSubmit} enableReinitialize>
          {({ values, setValues }) => (
            <Form className={styles.formCont}>
              <div className={styles.formInputSection}>
                <div className="form-group position-initial mb-2">
                  <Field
                    name="first_name"
                    id="first_name"
                    label={t('formLabels.firstName')}
                    as={CustomTextField}
                    CustomIcon={PersonIcon}
                  />
                </div>

                <div className="form-group position-initial mb-2">
                  <Field
                    name="last_name"
                    id="last_name"
                    label={t('formLabels.lastName')}
                    as={CustomTextField}
                    CustomIcon={PersonIcon}
                  />
                </div>

                <div className="form-group position-initial mb-2">
                  <Field
                    name="company_position"
                    id="company_position"
                    label={t('formLabels.companyPosition')}
                    as={CustomTextField}
                    CustomIcon={PositionIcon}
                  />
                </div>

                <div className="form-group position-initial mb-2">
                  <Field
                    value={values.location}
                    name="location"
                    as={LocationSelect}
                    type="text"
                    label={t('labels.location')}
                    onChange={(location) => {
                      setValues({
                        ...values,
                        location,
                      });
                    }}
                  />
                </div>

                <div className="form-group position-initial d-flex mb-2">
                  <div className="position-relative float-none col">
                    <Field
                      name={linkedin.length ? linkedin[0].type : linkedinAbbreviation}
                      id={linkedin.length ? linkedin[0].type : linkedinAbbreviation}
                      value={linkedin.length ? linkedin[0].value : ''}
                      placeholder="https://linkedin.com/in/"
                      as={CustomTextField}
                      onChange={(e) => handleLinkedinValue(e, linkedinAbbreviation)}
                      CustomIcon={ColoredLinkedlnIconImage}
                    />
                  </div>
                  {linkedin.length && linkedin[0].public === false ? (
                    <div className={styles.publicIcon}>
                      <PublicOffIconImage
                        className={styles.privateIconSvg}
                        onClick={() => handleLinkedinVisibilityChange(true)}
                      />
                    </div>
                  ) : (
                    <div className={styles.publicIcon}>
                      <PublicIconImage
                        className={styles.publicIconSvg}
                        onClick={() => handleLinkedinVisibilityChange(false)}
                      />
                    </div>
                  )}
                </div>
                {websiteLinks &&
                  websiteLinks.map(
                    (el) =>
                      el.type !== linkedinAbbreviation && (
                        <div
                          className={`form-group position-initial d-flex mb-2 ${styles.addedWebsites}`}
                          key={el.type}
                        >
                          <div className="position-relative col">
                            <Field
                              name={el.type}
                              id={el.type}
                              value={el.value}
                              placeholder={t('placeholders.addNewWebsite')}
                              as={CustomTextField}
                              onChange={(e) => handleChange(e, el.type)}
                            />
                          </div>
                          <div className={styles.crossIcon} aria-hidden="true">
                            <CrossIcon onClick={() => handleRemove(el.type)} className={styles.clickable} />
                          </div>
                          {el.public === false ? (
                            <div className={styles.publicIcon}>
                              <PublicOffIconImage
                                className={styles.privateIconSvg}
                                onClick={() => handleVisibilityChange(el.type, true)}
                              />
                            </div>
                          ) : (
                            <div className={styles.publicIcon}>
                              <PublicIconImage
                                className={styles.publicIconSvg}
                                onClick={() => handleVisibilityChange(el.type, false)}
                              />
                            </div>
                          )}
                        </div>
                      )
                  )}

                <div className="form-group position-initial d-flex mb-2">
                  <div className="position-relative col">
                    <Field
                      placeholder={t('placeholders.addNewWebsite')}
                      as={CustomTextField}
                      value={customWebsiteLink.value}
                      onChange={(e) => customWebsiteHandleChange(e, `wb_${nanoid()}`)}
                    />
                  </div>
                  <div className={styles.publicIcon} aria-hidden="true">
                    <PlusIcon className={styles.clickable} onClick={addNewWebsitelink} />
                  </div>
                  {customWebsiteLink.public === false ? (
                    <div className={styles.publicIcon}>
                      <PublicOffIconImage
                        className={styles.privateIconSvg}
                        onClick={() => setCustomWebsiteLink({ ...customWebsiteLink, public: true })}
                      />
                    </div>
                  ) : (
                    <div className={styles.publicIcon}>
                      <PublicIconImage
                        className={styles.publicIconSvg}
                        onClick={() => setCustomWebsiteLink({ ...customWebsiteLink, public: false })}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.personalUrlCont}>
                  <p className={styles.heading}>{t('profile.personalizeUrl')}</p>
                </div>

                <div className={styles.urlInputCont}>
                  <p className={styles.urlPrefix}>{FORMATTED_URL}/p/</p>
                  <div className={styles.inputCont}>
                    <Field
                      name="personal_url"
                      id="personal_url"
                      placeholder="customizable-portion"
                      as={CustomTextField}
                      error={isProfileURLDuplicate}
                      onFocus={handlePersonalURLFocus}
                    />
                    {isProfileURLDuplicate && <p className={styles.error}>{t('profile.urlInUse')}</p>}
                  </div>
                </div>
                <p className={styles.info}>{t('profile.urlRequirements')}</p>
              </div>
              <div className={styles.buttonContainer}>
                <Button
                  variant="primary"
                  type="submit"
                  className={styles[('saveBtn', 'footerButton')]}
                  disabled={!(values.first_name?.trim() && values.last_name?.trim() && values.location)}
                >
                  {t('save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
